import ExcelJS from 'exceljs';
import FileSaver from 'file-saver';
import moment from 'moment';
import { IIncomeInvoiceResourceShortProps } from '../../../../props/finance/incomes/invoices/invoice';
import IncomeInvoiceService from '../../../../services/finance/incomes/invoices/invoice';
import OrderService from '../../../../services/orders/order';

export const exportIncomeInvoicesToExcel = async (
    invoices: IIncomeInvoiceResourceShortProps[],
    customTitle?: string
) => {
    try {
        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'Jejak Logistik System';
        workbook.created = new Date();

        const worksheet = workbook.addWorksheet('Income Invoices');

        // Title Block
        worksheet.mergeCells('A1:M1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = customTitle || 'LAPORAN INCOME INVOICES - JEJAK LOGISTIK';
        titleCell.font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FF0284C7' } };
        titleCell.alignment = { horizontal: 'left', vertical: 'middle' };

        worksheet.mergeCells('A2:M2');
        const subtitleCell = worksheet.getCell('A2');
        subtitleCell.value = `Tanggal Export: ${moment().format('DD MMMM YYYY, HH:mm')} | Total: ${invoices.length} Invoice(s)`;
        subtitleCell.font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF555555' } };
        subtitleCell.alignment = { horizontal: 'left', vertical: 'middle' };

        // Empty row
        worksheet.addRow([]);

        // Header Row (Row 4)
        const headerTitles = [
            'No',
            'No. Invoice',
            'No. Order',
            'Customer / Perusahaan',
            'Perusahaan (Subsidiary)',
            'Sales',
            'Tanggal Invoice',
            'Tanggal Jatuh Tempo',
            'Catatan',
            'Total Tagihan (Rp)',
            'Paid Amount (Rp)',
            'Unpaid Amount (Rp)',
            'Status'
        ];

        const headerRow = worksheet.addRow(headerTitles);
        headerRow.height = 26;

        headerRow.eachCell((cell) => {
            cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF0284C7' } // Sky Blue Brand Header
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = {
                top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
                left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
                bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
                right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
            };
        });

        let grandTotalAmount = 0;
        let grandTotalPaid = 0;
        let grandTotalUnpaid = 0;

        // Data Rows
        invoices.forEach((item, index) => {
            const invoiceNum = '#' + IncomeInvoiceService.getInvoiceNumber(item.id || '');

            let orderNum = '-';
            if (item.order?.orderNumber) {
                orderNum = '#' + OrderService.getOrderNumber(String(item.order.orderNumber));
            }

            let customerName = '-';
            if (item.company?.name) {
                customerName = item.company.name;
            } else if (item.order?.customer?.name) {
                customerName = item.order.customer.name;
            } else if ((item.order as any)?.customer_name) {
                customerName = (item.order as any).customer_name;
            }

            const subsidiaryName = item.subsidiary?.name || item.order?.subsidiary?.name || '-';
            let salesName = '-';
            if (item.order?.sales) {
                if (Array.isArray(item.order.sales) && item.order.sales.length > 0) {
                    salesName = (item.order.sales[0] as any)?.name || (item.order.sales[0] as any)?.user?.name || '-';
                } else if ((item.order.sales as any)?.name) {
                    salesName = (item.order.sales as any).name;
                }
            }

            const invoiceDateStr = item.createdAt || item.paidOn || item.dueDate;
            const invoiceDateFormatted = invoiceDateStr ? moment(invoiceDateStr).format('DD/MM/YYYY') : '-';
            const dueDateFormatted = item.dueDate ? moment(item.dueDate).format('DD/MM/YYYY') : '-';

            const totalAmount = Number(item.invoiceAmount || item.totalAmount || item.amount || 0);
            const unpaidAmount = Number(item.unpaid || 0);
            const paidAmount = Number(item.paid || 0) || (totalAmount - unpaidAmount);

            grandTotalAmount += totalAmount;
            grandTotalPaid += paidAmount;
            grandTotalUnpaid += unpaidAmount;

            let statusText = item.status || 'Unpaid';
            if (statusText.toLowerCase() === 'cancelled') {
                statusText = 'Cancelled';
            } else if (unpaidAmount <= 0) {
                statusText = 'Paid';
            }

            const rowData = [
                index + 1,
                invoiceNum,
                orderNum,
                customerName,
                subsidiaryName,
                salesName,
                invoiceDateFormatted,
                dueDateFormatted,
                item.notes || '-',
                totalAmount,
                paidAmount,
                unpaidAmount,
                statusText
            ];

            const row = worksheet.addRow(rowData);
            row.height = 20;

            row.eachCell((cell, colNumber) => {
                cell.font = { name: 'Arial', size: 9 };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                    left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                    bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
                    right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
                };

                // Alignments & Number formats
                if (colNumber === 1 || colNumber === 2 || colNumber === 3 || colNumber === 7 || colNumber === 8 || colNumber === 13) {
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                } else if (colNumber >= 10 && colNumber <= 12) {
                    cell.alignment = { horizontal: 'right', vertical: 'middle' };
                    cell.numFmt = '#,##0';
                } else {
                    cell.alignment = { horizontal: 'left', vertical: 'middle' };
                }
            });
        });

        // Summary Total Row
        const summaryRowIndex = worksheet.rowCount + 1;
        worksheet.mergeCells(`A${summaryRowIndex}:I${summaryRowIndex}`);
        const totalRow = worksheet.getRow(summaryRowIndex);
        totalRow.height = 24;

        const labelCell = worksheet.getCell(`A${summaryRowIndex}`);
        labelCell.value = 'TOTAL';
        labelCell.font = { name: 'Arial', size: 10, bold: true };
        labelCell.alignment = { horizontal: 'right', vertical: 'middle' };

        const totalAmtCell = worksheet.getCell(`J${summaryRowIndex}`);
        totalAmtCell.value = grandTotalAmount;
        totalAmtCell.numFmt = '#,##0';
        totalAmtCell.font = { name: 'Arial', size: 10, bold: true };
        totalAmtCell.alignment = { horizontal: 'right', vertical: 'middle' };

        const paidAmtCell = worksheet.getCell(`K${summaryRowIndex}`);
        paidAmtCell.value = grandTotalPaid;
        paidAmtCell.numFmt = '#,##0';
        paidAmtCell.font = { name: 'Arial', size: 10, bold: true };
        paidAmtCell.alignment = { horizontal: 'right', vertical: 'middle' };

        const unpaidAmtCell = worksheet.getCell(`L${summaryRowIndex}`);
        unpaidAmtCell.value = grandTotalUnpaid;
        unpaidAmtCell.numFmt = '#,##0';
        unpaidAmtCell.font = { name: 'Arial', size: 10, bold: true };
        unpaidAmtCell.alignment = { horizontal: 'right', vertical: 'middle' };

        totalRow.eachCell((cell) => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF0F9FF' }
            };
            cell.border = {
                top: { style: 'medium', color: { argb: 'FFBAE6FD' } },
                bottom: { style: 'double', color: { argb: 'FF0284C7' } }
            };
        });

        // Auto-fit Column Widths
        worksheet.columns.forEach((column) => {
            let maxLen = 12;
            column.eachCell?.({ includeEmpty: false }, (cell) => {
                const len = cell.value ? cell.value.toString().length : 0;
                if (len > maxLen) { maxLen = len; }
            });
            column.width = Math.min(Math.max(maxLen + 3, 12), 45);
        });

        // Save to XLSX file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const fileName = `Income_Invoices_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
        FileSaver.saveAs(blob, fileName);
    } catch (error) {
        console.error('Failed to export income invoices to Excel', error);
        throw error;
    }
};
